import { useEffect, useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Icon, Link, Text, Tooltip } from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import classNames from 'classnames'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import keys from 'lodash/keys'
import map from 'lodash/map'
import omit from 'lodash/omit'
import size from 'lodash/size'
import uniq from 'lodash/uniq'
import values from 'lodash/values'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { formatDropdownOptions } from 'utils/formatters'
import {
  getEntriesToFetch,
  getLastPageFetched,
  getPage,
} from 'utils/pagination'
import { relayConnectionToArray } from 'utils/transformations/graphql'
import { fromGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { getPriceFromRange } from 'shop/products/getters'
import { EMPTY_CELL_CONTENT } from 'shop/products/utils'
import { isAccountTypeBrand } from 'store/currentUser/selectors'

import {
  Divider,
  Dropdown,
  Grid,
  HeaderAndValues,
  Loader,
  Modal,
  Pagination,
  ProductCard,
  Search,
} from 'components/Core'

import styles from './AddMoreProductsModal.module.css'
import { orderVariantTotalsQuery } from './AddMoreProductsModal.queries'

const Warning = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 0;
`

const StyledIcon = styled(Icon)`
  svg {
    --main-color: var(--color-secondary-800);
  }
`

export const StyledWarningIcon = styled(Icon)`
  svg {
    --main-color: var(--color-warning-700);
  }
`

const TooltipWrapper = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`

const AddMoreProductsModal = (props) => {
  const {
    collections,
    onClose,
    selectedCollectionId,
    setSearchText,
    setCollectionId,
    loading,
    products,
    orderCommonInfo,
    orderId,
    handleUpsertOrderQuantities,
    renderToasters: { renderSuccessToast, renderErrorToast },
    totalPages,
    hasNextPage,
    loadMoreEntries,
    pageSize,
    unavailableVariantIds,
  } = props

  const [selectedVariants, setSelectedVariants] = useState({})
  const [activePageNum, setActivePage] = useState(1)
  const [variantIdsInOrder, setVariantIdsInOrder] = useState([])
  const [fetchingMore, setFetchingMore] = useState(false)

  const isBrand = useSelector(isAccountTypeBrand)

  const { newCollectionsEditAndCreate } = useFlags()

  const productsInPage = getPage(products, activePageNum, pageSize)
  const { numSelectedProducts, numSelectedVariants } = useMemo(
    () => ({
      numSelectedProducts: size(uniq(values(selectedVariants))),
      numSelectedVariants: size(uniq(keys(selectedVariants))),
    }),
    [selectedVariants],
  )

  const {
    loading: orderVariantTotalsQueryLoading,
    data: orderVariantTotalsData,
  } = useQuery(orderVariantTotalsQuery, {
    variables: {
      orderIds: [orderId],
      filter: { productIds: productsInPage.map((product) => product.id) },
    },
    client: atlasClient,
    skip: !orderId || isEmpty(productsInPage),
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (!orderVariantTotalsQueryLoading) {
      const variantIds = relayConnectionToArray(
        get(orderVariantTotalsData, 'orderVariantTotals', []),
      ).map((orderVariantTotal) => orderVariantTotal.variantId)
      setVariantIdsInOrder(variantIds)
    }
  }, [orderVariantTotalsQueryLoading])

  useEffect(
    () => () => {
      setSearchText(null)
      setCollectionId(null)
    },
    [],
  )

  const handleSwatchSelected = (displayedSwatch, product) => {
    const variantId = get(displayedSwatch, 'color.id')
    const productId = get(product, 'id')
    if (variantId && productId) {
      if (selectedVariants[variantId]) {
        setSelectedVariants(omit(selectedVariants, variantId))
      } else {
        setSelectedVariants({
          ...selectedVariants,
          [variantId]: {
            productId,
            collectionId: selectedCollectionId,
          },
        })
      }
    }
  }

  const handleUpdateUnits = () => {
    handleUpsertOrderQuantities({
      bulkQuantities: map(
        selectedVariants,
        ({ productId, collectionId }, variantId) => ({
          productId,
          variantId,
          orderId,
          quantity: 0,
          collectionId,
        }),
      ),
    }).then(({ errors }) => {
      if (!errors) {
        renderSuccessToast({
          description: 'Everything added successfully to the order',
        })
      } else {
        renderErrorToast({
          description: 'There was an error in the process. Try again later.',
        })
      }
    })
  }

  const handlePaginationChange = (_, { activePage }) => {
    if (loading) {
      return
    }

    const lastPageFetched = getLastPageFetched(products, pageSize)
    const isAfterLastPageFetched = lastPageFetched < activePage

    if (isAfterLastPageFetched && hasNextPage) {
      const numberOfEntries = getEntriesToFetch(
        activePage,
        lastPageFetched,
        pageSize,
      )
      setFetchingMore(true)
      loadMoreEntries(numberOfEntries).then(() => setFetchingMore(false))
    }

    setActivePage(activePage)
  }

  const renderPrices = (product) => {
    const { retailRange, wholesaleRange } = product
    const hasWholesalePrice = Boolean(wholesaleRange.min)
    const wholesaleValue = getPriceFromRange(
      wholesaleRange,
      orderCommonInfo?.priceType,
    )
    const retailPlaceHolder = hasWholesalePrice ? EMPTY_CELL_CONTENT : null
    const retailValue = getPriceFromRange(
      retailRange,
      orderCommonInfo?.priceType,
      retailPlaceHolder,
    )

    return (
      <>
        <HeaderAndValues
          translateValues={wholesaleRange.min === null}
          header="Wholesale"
          values={[wholesaleValue]}
          className={classNames([
            styles.price,
            { [styles.unavailable]: wholesaleRange.min === null },
          ])}
        />
        <HeaderAndValues
          translateValues={retailRange.min === null}
          header="Suggested Retail"
          values={[retailValue]}
          className={classNames([
            styles.price,
            {
              [styles.unavailable]:
                !hasWholesalePrice && retailRange.min === null,
            },
          ])}
        />
        <Divider verticalMargin={10} horizontalMargin={0} />
      </>
    )
  }

  const renderFabrication = (product) => (
    <>
      <HeaderAndValues
        header="Fabrication"
        values={[product.fabrication]}
        className={styles.unitsAndFabrication}
      />
    </>
  )

  const formattedCollections = formatDropdownOptions(
    collections,
    undefined,
    false,
    'notranslate',
  )

  const collection = collections.find(
    (collection) => collection.id === selectedCollectionId,
  )

  // we know orphan orders doesnt return the isVisibleToRetailer because to ask for that node,
  // we need a retailer id (which orphan order dont have)
  const isNotOrphan =
    collection &&
    Object.keys(collection).some((key) => key === 'isVisibleToRetailer')

  const linesheetLink = newCollectionsEditAndCreate
    ? `${window.location.origin}/ra/linesheet/${
        fromGlobalId(collection?.id).id
      }`
    : `${window.location.origin}/collections/view/${
        fromGlobalId(collection?.id).id || ''
      }?brandId=${fromGlobalId(orderCommonInfo?.brand?.id).id}`

  return (
    <Modal
      title={`Add Products (${numSelectedProducts} Style(s) / ${numSelectedVariants} Style color(s) selected)`}
      primaryActionLabel="Add to Order"
      open
      onClose={onClose}
      className={styles.AddMoreProductsModal}
      primaryDisabled={isEmpty(selectedVariants)}
      primaryActionOnClick={handleUpdateUnits}
    >
      <Loader
        active={loading || orderVariantTotalsQueryLoading || fetchingMore}
        useFullScreen
      />
      <div className={styles.filters}>
        <TooltipWrapper>
          <Dropdown
            id="collectionOptions"
            onChange={(_, { value }) => {
              setCollectionId(value)
              setActivePage(1)
            }}
            options={formattedCollections}
            search
            value={selectedCollectionId}
            /* TODO (ch79600): add logic for notranslate classname with
      selectedCollectionId && selectedCollectionId !== ALL_COLLECTIONS */
            className={classNames(styles.dropdown, 'notranslate')}
          />
          {!isBrand && (
            <Tooltip
              defaultPlacement="right"
              content="You can only select linesheets that have been shared with you. 
              If you believe a linesheet is missing, please reach out to the brand directly."
            >
              <StyledIcon iconName="question" iconSize="regular" />
            </Tooltip>
          )}
        </TooltipWrapper>
        <Search
          className={styles.search}
          handleAction={(value) => {
            setSearchText(value)
            setActivePage(1)
          }}
          inputOnly
        />
      </div>
      {!collection?.isVisibleToRetailer && isBrand && isNotOrphan && (
        <Warning>
          <StyledWarningIcon iconName="warning" iconSize="regular" plain />
          <Text
            fontFamily="alpha"
            fontSize="small"
            color="warning-700"
            fontWeight="regular"
            letterSpacing="wider"
            lineHeight="16"
          >
            <span>
              This linesheet has not been shared with this retailer. You can
              continue to add items from it without changing the visibility or
              you can edit the settings of{' '}
              <Link
                href={linesheetLink}
                target="_blank"
                fontFamily="numeric"
                fontSize="base"
                fontWeight="semibold"
                lineHeight="16"
                letterSpacing="normal"
                className="notranslate"
              >
                {collection?.name}
              </Link>
              . Continuing without changing the visibility may limit your
              retailer’s access to product information.
            </span>
          </Text>
        </Warning>
      )}
      <Grid className={isEmpty(products) ? styles.noProducts : ''}>
        <Grid.Row alignColumns="left">
          {productsInPage.map((product) => (
            <Grid.Col
              sm={4}
              md={3}
              lg={3}
              key={product.id}
              className={styles.cardContainer}
            >
              <ProductCard
                product={product}
                displayCheckboxes
                checkboxOnClick={(displayedSwatch) =>
                  handleSwatchSelected(displayedSwatch, product)
                }
                selectedVariants={selectedVariants}
                disabledVariants={[
                  ...variantIdsInOrder,
                  ...unavailableVariantIds,
                ]}
              >
                {renderPrices(product)}
                {renderFabrication(product)}
              </ProductCard>
            </Grid.Col>
          ))}
          {isEmpty(products) && (
            <span className={styles.noResults}>
              No results found. Please try changing your search or filter
              criteria.
            </span>
          )}
        </Grid.Row>
      </Grid>
      {!isEmpty(products) && (
        <div className={styles.pagination}>
          <Pagination
            onPageChange={handlePaginationChange}
            totalPages={totalPages}
            activePage={activePageNum}
          />
        </div>
      )}
    </Modal>
  )
}
AddMoreProductsModal.propTypes = {
  onClose: PropTypes.func,
  collections: PropTypes.array.isRequired,
  selectedCollectionId: PropTypes.string,
  loading: PropTypes.bool,
  setSearchText: PropTypes.func.isRequired,
  setCollectionId: PropTypes.func.isRequired,
  products: PropTypes.array,
  orderCommonInfo: PropTypes.object.isRequired,
  handleUpsertOrderQuantities: PropTypes.func.isRequired,
  orderId: PropTypes.string.isRequired,
  renderToasters: PropTypes.func,
  totalPages: PropTypes.number.isRequired,
  loadMoreEntries: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  unavailableVariantIds: PropTypes.array.isRequired,
}

AddMoreProductsModal.defaultProps = {
  onClose: () => {},
  loading: false,
  selectedCollectionId: null,
  products: [],
  renderToasters: () => {},
}

export default AddMoreProductsModal
